import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FormFieldInput,
  LinkButton,
  FormFieldPhoneInput,
  LoadingIndicator,
  countries,
  PhoneDigitsIsValid,
  FormFieldDropdownInput,
  usePersistForm,
  FormFieldUrlInput,
  InfoTip,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  CountryCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'libphonenumber-js';
import {
  FieldError,
  FieldErrors,
  Resolver,
  ResolverOptions,
  useForm,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { SignUpStep } from '../../common/provider/SignUpContext';
import { useSignUp } from '../../common/hooks/useSignUp';
import { postOrgDetails } from '../../common/api/sign-up';
import { OrgDetailsForm } from './types';
import { ApiErrorResponse } from '../../common/api/types';
import {
  SIGN_UP_ORG_DETAIL_FORM,
  SIGN_UP_SELECTED_SOLUTION_TYPE,
  SIGN_UP_SELECTED_TYPE,
  domainName,
  urlTooltip,
  whitelistedStatusCodes,
} from '../../common/constants';
import {
  composeShortNameFromCompanyName,
  isCountryIndia,
  stepRouteMapping,
} from '../signUpUtility';
import { ActionBar } from './ActionBar';
import { Header } from './Header';
import { InputLabelTitle } from './InputLabelTitle';

const initalFormValues: OrgDetailsForm = {
  firstName: '',
  lastName: '',
  companyName: '',
  country: '',
  mobileNumber: '',
  orgDomainName: '',
};

const nameValidation = (value: string | boolean, label: string) => {
  if ((value as string).length === 0) {
    return `${label} is required.`;
  } else if ((value as string).length > 36) {
    return 'Max of 36 characters.';
  } else if (
    !new RegExp(/^[a-zA-Z][-a-zA-Z ]{0,34}[a-zA-Z]$/i).test(value as string)
  ) {
    return 'Name should be composed of 2 to 36 alphabetic characters,hyphens and space only';
  }

  return true;
};

export const orgDetailsFormResolver: Resolver<OrgDetailsForm> = (
  values,
  { displayUrl }
) => {
  const errors = {} as FieldErrors<OrgDetailsForm>;

  const firstNameValidation = nameValidation(values.firstName, 'First Name');
  if (typeof firstNameValidation === 'string') {
    errors.firstName = {
      message: firstNameValidation,
    } as FieldError;
  }

  const lastNameValidation = nameValidation(values.lastName, 'Last Name');
  if (typeof lastNameValidation === 'string') {
    errors.lastName = {
      message: lastNameValidation,
    } as FieldError;
  }

  if (!values.companyName) {
    errors.companyName = {
      message: 'Company Name is required',
    } as FieldError;
  } else if (values.companyName.length < 3) {
    errors.companyName = {
      message: 'Min 3 characters',
    } as FieldError;
  } else if (values.companyName.length > 100) {
    errors.companyName = {
      message: 'Max 100 characters',
    } as FieldError;
  } else if (
    !new RegExp(/^[a-zA-Z0-9\s@&-]{3,100}$/).test(values.companyName)
  ) {
    errors.companyName = {
      message: 'Can only contain alphanumeric characters, &, - and @',
    } as FieldError;
  }

  if (displayUrl) {
    if (!values.orgDomainName) {
      errors.orgDomainName = {
        message: 'URL is required',
      } as FieldError;
    } else if (values.orgDomainName.length < 3) {
      errors.orgDomainName = {
        message: 'Min 3 characters',
      } as FieldError;
    } else if (values.orgDomainName.length > 63) {
      errors.orgDomainName = {
        message: 'Max 63 characters',
      } as FieldError;
    } else if (
      !new RegExp(/^(?!-)[a-zA-Z0-9-]{3,63}(?<!-)$/).test(values.orgDomainName)
    ) {
      errors.orgDomainName = {
        message:
          'Can only contain alphanumeric characters or hyphens ("-") in-between.',
      } as FieldError;
    } else if (values.orgDomainName.includes('---')) {
      errors.orgDomainName = {
        message: 'Only two hyphens are allowed consecutively.',
      } as FieldError;
    }
  }

  if (!values.country) {
    errors.country = {
      message: 'Country is required',
    } as FieldError;
  }

  if (!values.mobileNumber) {
    errors.mobileNumber = {
      message: 'Mobile number is required',
    } as FieldError;
  } else if (
    !isValidPhoneNumber(
      values.mobileNumber as string,
      values.country as CountryCode
    )
  ) {
    errors.mobileNumber = {
      message: 'Invalid number',
    } as FieldError;
  } else if (!PhoneDigitsIsValid(values.mobileNumber)) {
    errors.mobileNumber = {
      message: 'Same number for all digits is not allowed',
    } as FieldError;
  }

  return { values, errors };
};

export const OrgDetailsComponent: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    formId,
    areOrgDetailsValid,
    signUpCustomerCountry,
    setSignUpStep,
    setSignUpCustomerCountry,
    setErrorMessage,
    setAreOrgDetailsValid,
  } = useSignUp();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isUrlEmpty, setIsUrlEmpty] = useState<boolean>(false);

  const displayUrl = useMemo(() => {
    const solutionType = sessionStorage.getItem(
      SIGN_UP_SELECTED_SOLUTION_TYPE
    ) as string;
    const type = sessionStorage.getItem(SIGN_UP_SELECTED_TYPE) as string;
    if (solutionType === '1' && type === 'SOLUTION') {
      return true;
    }
    return false;
  }, []);

  const getSavedData = useCallback(() => {
    const rawData = sessionStorage.getItem(SIGN_UP_ORG_DETAIL_FORM);
    if (rawData) {
      const dataObject: OrgDetailsForm = JSON.parse(rawData);
      return dataObject;
    } else {
      if (signUpCustomerCountry) {
        const newForm: OrgDetailsForm = { ...initalFormValues };
        newForm.country = signUpCustomerCountry;
        return newForm;
      } else {
        return { ...initalFormValues };
      }
    }
  }, [signUpCustomerCountry]);

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setError,
    setValue,
    formState: { errors, isValid },
  } = useForm<OrgDetailsForm>({
    mode: 'onChange',
    defaultValues: getSavedData(),
    resolver: (values) =>
      orgDetailsFormResolver(
        values,
        { displayUrl },
        {} as ResolverOptions<OrgDetailsForm>
      ),
  });
  const countryCode = watch('country');
  const companyName = watch('companyName');
  const orgDomainName = watch('orgDomainName');
  const countryIsIndia = useMemo(() => {
    return isCountryIndia(countryCode as string);
  }, [countryCode]);

  useEffect(() => {
    if (!displayUrl)
      setValue('orgDomainName', '', {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
  }, [displayUrl, setValue]);

  useEffect(() => {
    setSignUpCustomerCountry(countryCode);
  }, [countryCode, setSignUpCustomerCountry]);

  useEffect(() => {
    if (!countryCode && signUpCustomerCountry) {
      setValue('country', signUpCustomerCountry);
    }
  }, [setValue, signUpCustomerCountry, countryCode]);

  const navigateToNextStep = useCallback(() => {
    setAreOrgDetailsValid(true);
    const signUpStep = countryIsIndia
      ? SignUpStep.taxIndiaDetails
      : SignUpStep.billingDetails;
    setSignUpStep(signUpStep);
    navigate(stepRouteMapping[SignUpStep[signUpStep]]);
  }, [setSignUpStep, setAreOrgDetailsValid, navigate, countryIsIndia]);

  const onSubmit = handleSubmit(
    async ({
      firstName,
      lastName,
      companyName,
      country,
      mobileNumber,
      orgDomainName,
    }: OrgDetailsForm) => {
      try {
        const phoneNumber = parsePhoneNumber(
          mobileNumber,
          country as CountryCode
        );
        setIsLoading(true);
        mobileNumber = `+${phoneNumber.countryCallingCode}${mobileNumber}`;
        if (!areOrgDetailsValid) {
          const response = await postOrgDetails(
            formId as string,
            firstName,
            lastName,
            companyName,
            mobileNumber,
            displayUrl ? orgDomainName : ''
          );
          if (
            !response?.validation?.valid &&
            response?.validation?.violations
          ) {
            response.validation.violations.forEach((violation) =>
              setError(violation.element as keyof OrgDetailsForm, {
                message: violation.violation,
              })
            );
          } else if (response.contactAdmin) {
            setSignUpStep(SignUpStep.contactAdmin);
            navigate(stepRouteMapping[SignUpStep[SignUpStep.contactAdmin]]);
          } else {
            navigateToNextStep();
          }
        } else {
          navigateToNextStep();
        }
        setIsLoading(false);
      } catch (e: any) {
        if (whitelistedStatusCodes.includes(e.request.status)) {
          const errorResponse: ApiErrorResponse = JSON.parse(
            e.request.response
          );
          setErrorMessage(errorResponse.error.description);
        } else {
          setErrorMessage('Please go back to the form and try again');
        }
        setIsLoading(false);
      }
    }
  );

  const onTermsAndConditionsClick = useCallback(() => {
    window.open('https://getos1.com/subscriber-terms');
  }, []);

  const onPrivacyPolicyClick = useCallback(() => {
    window.open('https://getos1.com/privacy');
  }, []);

  usePersistForm({
    value: getValues(),
    sessionStorageKey: SIGN_UP_ORG_DETAIL_FORM,
  });

  const handleFocus = useCallback(() => {
    if (displayUrl) {
      if (orgDomainName) setIsUrlEmpty(false);
      else setIsUrlEmpty(true);
    }
  }, [orgDomainName, displayUrl]);

  const handleCompanyKeyUp = useCallback(() => {
    if (displayUrl && isUrlEmpty)
      setValue('orgDomainName', composeShortNameFromCompanyName(companyName), {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
  }, [isUrlEmpty, setValue, companyName, displayUrl]);

  if (isLoading || !countryCode) {
    return (
      <div className="flex flex-col w-full h-full justify-center items-center ">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:h-[100vh] w-full items-center content-between">
      <Header
        subtitle="Nice to meet you! Tell us a little about yourself and where you work."
        title="Create your account"
      />
      <form className="flex-1 grid grid-cols-12 w-full pb-8 px-8 xl:px-0 font-opensans lg:overflow-auto lg:mb-56">
        <div className="col-start-1 xl:col-start-2 col-span-10 flex flex-col gap-4 h-full">
          <FormFieldInput
            errors={errors}
            inputKey="firstName"
            register={register}
            registerProps={{
              required: true,
              minLength: {
                value: 2,
                message: 'Name must be at least 2 characters.',
              },
            }}
            testId="FirstName-input"
            className="w-full pb-4"
            inputClassName="shadow"
            title={
              <InputLabelTitle
                title="First Name"
                hasError={Boolean(errors?.firstName)}
                required
              />
            }
          />
          <FormFieldInput
            errors={errors}
            inputKey="lastName"
            register={register}
            registerProps={{
              required: true,
              minLength: {
                value: 2,
                message: 'Name must be at least 2 characters.',
              },
            }}
            testId="LastName-input"
            className="w-full pb-4"
            inputClassName="shadow"
            title={
              <InputLabelTitle
                title="Last Name"
                hasError={Boolean(errors?.lastName)}
                required
              />
            }
          />
          <FormFieldInput
            errors={errors}
            inputKey="companyName"
            register={register}
            registerProps={{
              required: true,
              onChange: () => {
                setAreOrgDetailsValid(false);
              },
            }}
            testId="companyName-input"
            className="w-full pb-4"
            inputClassName="shadow"
            title={
              <InputLabelTitle
                title="Company Name"
                hasError={Boolean(errors?.companyName)}
                required
              />
            }
            onFocus={handleFocus}
            onKeyUp={handleCompanyKeyUp}
          />
          {displayUrl && (
            <div className="flex flex-row">
              <FormFieldUrlInput
                errors={errors}
                inputKey="orgDomainName"
                register={register}
                registerProps={{
                  required: true,
                }}
                testId="url-input"
                className="w-full pb-4"
                inputClassName="shadow rounded-r-0 rounded-l-md"
                placeholder="Enter URL"
                domainName={domainName}
                title={
                  <InputLabelTitle
                    title="URL"
                    hasError={Boolean(errors?.orgDomainName)}
                    required
                  />
                }
              />
              <span
                className={`self-center ml-3 ${
                  errors.orgDomainName?.message ? 'mb-3' : 'mt-3'
                }`}
              >
                <InfoTip
                  id="orgDomainName"
                  tip={{ title: '', content: textWrap(urlTooltip) }}
                  colorClass="text-primary-800"
                />
              </span>
            </div>
          )}
          <FormFieldDropdownInput
            title={
              <InputLabelTitle
                title="Country"
                hasError={Boolean(errors?.country)}
                required
              />
            }
            className="pb-2"
            errors={errors}
            inputKey="country"
            options={countries.map(({ code, name }) => ({
              id: code,
              display: name,
            }))}
            disabled={true}
            testId="country-select"
            register={register}
            required={true}
          />
          <FormFieldPhoneInput
            errors={errors}
            inputKey="mobileNumber"
            register={register}
            registerProps={{
              required: true,
            }}
            divClassName="w-full"
            testId="mobileNumber-input"
            title={
              <InputLabelTitle
                title="Mobile Number"
                hasError={Boolean(errors?.mobileNumber)}
                required
              />
            }
            countryCode={countryCode as CountryCode}
          />
          <div className="flex flex-row items-center w-11/12 mt-os1-spacing">
            <span className="text-sm">
              By clicking "continue", you agree to the OS1&nbsp;
              <LinkButton
                linkText="Terms & Conditions "
                onClick={onTermsAndConditionsClick}
              />{' '}
              <span className="text-sm">and </span>
              <LinkButton
                linkText="Privacy Policy"
                onClick={onPrivacyPolicyClick}
              />
              <span className="text-sm">.</span>
            </span>
          </div>
        </div>
      </form>
      <ActionBar onSubmit={onSubmit} isValid={isValid} />
    </div>
  );
};

const textWrap = (text: string, maxLine = 100) => {
  let pointer = 0;
  let spacePointer = 0;
  let lastLineBreak = 0;
  let lines = [];

  while (pointer <= text.length - 1) {
    if (text[pointer] === ' ') {
      spacePointer = pointer;
    }

    if (pointer - lastLineBreak === maxLine) {
      lines.push(text.slice(lastLineBreak, spacePointer));
      lastLineBreak = spacePointer;
    }

    pointer += 1;
  }

  if (pointer - lastLineBreak > 0) {
    lines.push(text.slice(lastLineBreak, pointer));
  }

  return (
    <>
      {lines.map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    </>
  );
};
